import { useMemo } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { Lightbulb } from '@factorialco/f0-react/icons/app'
import { OneOnOnesChatDirectReportsDocument } from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useAiAgentFeature } from 'modules/ai/hooks/useAiAgentFeature'
import { AiAgentStateSync } from 'modules/meetings/components/AiAgentStateSync'
import { PageSkeleton } from 'modules/meetings/components/Skeletons/PageSkeleton'
import CollectionsBootstrap from 'modules/meetings/lib/OneononesroutesCollectionsBootstrap'
import { OneOnOnesContextProvider } from 'modules/meetings/lib/context/OneOnOnesContext/OneOnOnesProvider'
import { useAiChatAgent } from 'modules/meetings/lib/hooks/useAiChatAgent'
import { useOneOnOnesContext } from 'modules/meetings/lib/hooks/useOneOnOnesContext'
import { buildMeetingParticipants } from 'modules/meetings/lib/utils/chatAgentUtils'

const MEETINGS_AGENT = 'talent/engagement/meetings/chat'

const OneOnOnesRootPageWrapper = () => {
  const { canSeeMeetings, loading: contextLoading } = useOneOnOnesContext()
  const { employee: currentEmployee } = useCurrentSession()
  const { meetingId: meetingIdParam } = useParams<{ meetingId?: string }>()
  const { enabled: isAiEnabled, loading: aiLoading } = useAiAgentFeature()

  const { data, loading: directReportsLoading } = useQuery(OneOnOnesChatDirectReportsDocument, {
    variables: {
      employeeId: currentEmployee?.id ?? 0,
    },
    skip: !currentEmployee?.id,
  })

  const { managedEmployees, peers, mostRecentMeetingId } = useMemo(
    () => buildMeetingParticipants(data, currentEmployee?.id ?? 0),
    [data, currentEmployee?.id]
  )

  // Use URL param if present, otherwise use most recent meeting when on overview page
  const meetingId = meetingIdParam ? Number(meetingIdParam) : mostRecentMeetingId

  useAiChatAgent({
    agent: MEETINGS_AGENT,
    initialMessage: i18n.t('meetings.one_on_ones.chat.initial_message'),
    welcomeScreenSuggestions: [
      {
        icon: Lightbulb,
        message: i18n.t('meetings.one_on_ones.chat.suggestions.recent_insights'),
      },
      {
        icon: Lightbulb,
        message: i18n.t('meetings.one_on_ones.chat.suggestions.performance_review'),
      },
      {
        icon: Lightbulb,
        message: i18n.t('meetings.one_on_ones.chat.suggestions.upcoming_tasks'),
      },
    ],
    moduleBasePath: '/one-on-ones',
  })

  if (contextLoading || directReportsLoading) {
    return <PageSkeleton />
  }

  const authorizeFn = () => {
    return canSeeMeetings
  }

  return (
    <AuthorizeUser authorizeFn={authorizeFn}>
      {!aiLoading && isAiEnabled && (
        <AiAgentStateSync
          agent={MEETINGS_AGENT}
          meetingId={meetingId}
          managedEmployees={managedEmployees}
          peers={peers}
        />
      )}
      <Outlet />
    </AuthorizeUser>
  )
}

const OneOnOnesRootPage = (): JSX.Element => {
  return (
    <CollectionsBootstrap>
      <OneOnOnesContextProvider>
        <OneOnOnesRootPageWrapper />
      </OneOnOnesContextProvider>
    </CollectionsBootstrap>
  )
}

export default OneOnOnesRootPage
